import { Component, computed, inject, input, model, ChangeDetectionStrategy } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ShInputTextComponent, ShSelectComponent, ShSelectOption, TextValueAccessor } from '@gbr-monorepo/gbr-shui';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ApplicationStatus, CommentFilter } from '@gbr-monorepo/interfaces';
import { PayoutPeriodOperations } from '../../../../operations/payout-period.operations';
import { ApplicationFilter } from './application-filter.model';

@Component({
    selector: 'app-season-applications-filter',
    templateUrl: './applications-filter.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        TranslateModule,
        ReactiveFormsModule,
        TextValueAccessor,
        ShInputTextComponent,
        ShSelectComponent,
        FormsModule
    ]
})
export class ApplicationsFilterComponent {
    private readonly translate = inject(TranslateService);

    public readonly filters = model<ApplicationFilter>({} as ApplicationFilter);
    public readonly applicationFilterStates = input<ApplicationStatus[]>(Object.values(ApplicationStatus));
    public readonly applicationFilterComments = input<CommentFilter[]>(Object.values(CommentFilter));
    public readonly payoutPeriodOptions = input<string[]>([]);
    public readonly payoutPeriodOptionsLoading = input<boolean>(false);

    public readonly selectedStatuses = computed(() => this.filters()?.status ?? []);
    public readonly selectedCommentFilter = computed<CommentFilter>(
        () => this.filters()?.commentFilter ?? CommentFilter.ALL
    );
    public readonly selectedPayoutPeriods = computed(() => this.filters()?.payoutPeriods ?? []);
    public readonly statusOptions = computed<ShSelectOption[]>(() =>
        this.applicationFilterStates().map(status => ({
            value: status,
            label: 'seasons.season-details.application-status.' + status
        }))
    );
    public readonly commentOptions = computed<ShSelectOption[]>(() =>
        this.applicationFilterComments().map(comment => ({
            value: comment,
            label: 'seasons.season-details.comment-filters.' + comment
        }))
    );
    public readonly payoutPeriodSelectOptions = computed<ShSelectOption[]>(() =>
        this.payoutPeriodOptions().map(period => {
            const label = PayoutPeriodOperations.getLabelFromInterval(period);
            return {
                value: period,
                label: this.translate.instant(label.key, label.params)
            };
        })
    );

    protected onSearchTermChange(event: Event): void {
        const inputElement = event.target as HTMLInputElement;
        this.patchFilters({ searchTerm: inputElement.value });
    }

    protected onStatusFilterChange(values: string[] | string | null): void {
        this.patchFilters({ status: this.toArray(values) as ApplicationStatus[] });
    }

    protected onCommentFilterChange(value: string | string[] | null): void {
        const selected = Array.isArray(value) ? value[0] : value;

        this.patchFilters({ commentFilter: (selected as CommentFilter) ?? CommentFilter.ALL });
    }

    protected onPayoutPeriodFilterChange(values: string[] | string | null): void {
        this.patchFilters({ payoutPeriods: this.toArray(values) });
    }

    private patchFilters(next: Partial<ApplicationFilter>): void {
        this.filters.update(current => ({
            ...current,
            ...next
        }));
    }

    private toArray(values: string[] | string | null): string[] {
        if (Array.isArray(values)) {
            return values;
        }
        if (values) {
            return [values];
        }
        return [];
    }
}
